/**
 * Foundation/Core — SuiteLauncher (Hub Applicazioni)
 *
 * Launcher ufficiale della Gruppo Impresa Suite. Nessun riferimento al dominio
 * di GI Presenze o di altri Product Pack. Il componente e' parametrizzato via prop:
 *
 * - `modules`: elenco delle applicazioni della Suite (default: `defaultSuiteModules()`)
 * - `currentModuleKey`: chiave del modulo corrente (opzionale). Se presente,
 *   viene evidenziato con badge "Sei già qui" e mostrato nel footer con la sua
 *   versione (da `/api/system/version`).
 * - `onNavigate`: callback opzionale chiamata dopo l'apertura di un modulo.
 *
 * Endpoint utilizzato: `GET /api/system/version` (Foundation).
 * Richiede: `@/foundation/api/client` (barrel Foundation) e
 * `@/foundation/formatting/dates` (barrel Foundation).
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Grid2X2,
  XCircle,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { api } from "@/foundation/api/client";
import { formatDateIT } from "@/foundation/formatting/dates";
import { defaultSuiteModules } from "./suiteLauncherCatalog";

function ModuleCard({ module, current, onOpen }) {
  const Icon = module.icon;
  const canOpen = module.status === "active" && !current;

  const openModule = () => {
    if (!canOpen) return;
    window.open(module.url, "_blank", "noopener,noreferrer");
    onOpen?.();
  };

  return (
    <article
      data-testid={`suite-card-${module.key}`}
      data-status={current ? "current" : module.status}
      className={
        "flex min-h-[145px] flex-col rounded-xl border p-3 transition " +
        (current
          ? "border-suite-primary/50 bg-suite-primary/[0.045] shadow-sm ring-1 ring-suite-primary/15"
          : "border-suite-neutral-200 bg-white hover:border-suite-neutral-300 hover:shadow-sm")
      }
    >
      <div className="mb-2 flex items-start gap-2.5">
        <div
          aria-hidden="true"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-white shadow-sm"
          style={{ backgroundColor: module.color }}
        >
          <Icon size={18} strokeWidth={2} />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[13px] font-semibold text-suite-neutral-900">
            {module.name}
          </h3>
          <span
            data-testid={`suite-status-${module.key}`}
            className={
              "mt-1 inline-flex items-center gap-1 rounded-full border px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wider " +
              (current
                ? "border-emerald-200 bg-emerald-100 text-emerald-800"
                : module.status === "active"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                  : "border-rose-200 bg-rose-50 text-rose-700")
            }
          >
            <span
              aria-hidden="true"
              className={
                "h-1.5 w-1.5 rounded-full " +
                (module.status === "active" ? "bg-emerald-500" : "bg-rose-500")
              }
            />
            {current
              ? "Applicazione corrente"
              : module.status === "active"
                ? "Disponibile"
                : "Non disponibile"}
          </span>
        </div>
      </div>

      <p className="mb-3 min-h-[2.4em] text-[11px] leading-snug text-suite-neutral-500">
        {module.tagline}
      </p>

      <div className="mt-auto">
        {current ? (
          <Button
            type="button"
            disabled
            data-testid={`suite-btn-${module.key}`}
            className="h-8 w-full cursor-default gap-1.5 bg-emerald-50 text-[11px] font-semibold text-emerald-800 shadow-none hover:bg-emerald-50"
          >
            <CheckCircle2 size={12} strokeWidth={2.4} />
            Sei già qui
          </Button>
        ) : canOpen ? (
          <Button
            type="button"
            onClick={openModule}
            data-testid={`suite-btn-${module.key}`}
            className="h-8 w-full gap-1.5 text-[11px] font-semibold text-white"
            style={{ backgroundColor: module.color }}
            title={`Apri ${module.name} in una nuova scheda`}
          >
            Apri
            <ArrowRight size={12} strokeWidth={2.4} />
            <ExternalLink size={10} strokeWidth={2.4} className="opacity-80" />
          </Button>
        ) : (
          <Button
            type="button"
            disabled
            data-testid={`suite-btn-${module.key}`}
            variant="outline"
            className="h-8 w-full cursor-not-allowed gap-1.5 text-[11px] font-semibold text-suite-neutral-400"
          >
            <XCircle size={12} strokeWidth={2.4} />
            Non disponibile
          </Button>
        )}
      </div>
    </article>
  );
}

export function SuiteLauncher({ modules, currentModuleKey, onNavigate }) {
  const [open, setOpen] = useState(false);
  const [suiteMeta, setSuiteMeta] = useState(null);

  const resolvedModules = useMemo(
    () => (Array.isArray(modules) && modules.length ? modules : defaultSuiteModules()),
    [modules]
  );
  const availableCount = resolvedModules.filter((m) => m.status === "active").length;
  const currentModule = useMemo(
    () => (currentModuleKey ? resolvedModules.find((m) => m.key === currentModuleKey) : null),
    [resolvedModules, currentModuleKey]
  );

  useEffect(() => {
    if (!open || suiteMeta) return undefined;
    let alive = true;
    api.get("/system/version")
      .then(({ data }) => {
        if (alive) setSuiteMeta(data);
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [open, suiteMeta]);

  const closeAfterNavigation = () => {
    setOpen(false);
    onNavigate?.();
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button
          type="button"
          className="mx-3 mt-3 flex h-9 items-center gap-2 rounded-lg border border-suite-neutral-200 bg-white px-3 text-xs font-medium text-suite-neutral-700 shadow-sm transition hover:border-suite-primary/40 hover:bg-suite-primary/5 hover:text-suite-primary data-[state=open]:border-suite-primary/50 data-[state=open]:text-suite-primary"
          data-testid="suite-applications-button"
          aria-label="Apri Hub Applicazioni Gruppo Impresa Suite"
          title="Apri Hub Applicazioni"
        >
          <Grid2X2 size={14} />
          <span>Applicazioni</span>
          <span className="ml-auto text-[9px] font-semibold uppercase tracking-wider text-suite-neutral-400">
            Suite
          </span>
        </button>
      </DialogTrigger>

      <DialogContent
        overlayClassName="bg-black/45 backdrop-blur-[1px]"
        data-testid="suite-applications-hub"
        className="max-h-[90vh] max-w-2xl gap-0 overflow-hidden border-suite-neutral-200 p-0 shadow-2xl"
      >
        <DialogHeader className="border-b border-suite-neutral-100 bg-gradient-to-br from-suite-primary/5 to-white px-6 py-4">
          <div className="flex items-center gap-3">
            <div
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-suite-primary/10 text-suite-primary"
            >
              <Grid2X2 size={18} strokeWidth={2} />
            </div>
            <div className="min-w-0 flex-1">
              <DialogTitle className="text-[15px] font-semibold text-suite-neutral-900">
                Hub Applicazioni
              </DialogTitle>
              <DialogDescription className="mt-0.5 text-[11px] font-semibold uppercase tracking-[0.15em] text-suite-primary">
                Gruppo Impresa Suite
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="max-h-[62vh] overflow-y-auto bg-suite-neutral-50/40 px-4 py-4">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
            {resolvedModules.map((module) => (
              <ModuleCard
                key={module.key}
                module={module}
                current={module.key === currentModuleKey}
                onOpen={closeAfterNavigation}
              />
            ))}
          </div>
        </div>

        <footer className="border-t border-suite-neutral-100 bg-white px-6 py-3">
          <div className="mb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-suite-neutral-600">
            Ecosistema Gruppo Impresa Suite
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-suite-neutral-500">
            <span>
              <strong className="font-semibold text-suite-neutral-700">{resolvedModules.length}</strong>{" "}
              moduli nel catalogo
            </span>
            <span aria-hidden="true" className="text-suite-neutral-300">·</span>
            <span>
              <strong className="font-semibold text-emerald-700">{availableCount}</strong>{" "}
              disponibili
            </span>
            {currentModule && suiteMeta?.version && (
              <>
                <span aria-hidden="true" className="text-suite-neutral-300">·</span>
                <span>
                  {currentModule.name}{" "}
                  <strong className="font-semibold text-suite-neutral-700">
                    v{suiteMeta.version}
                  </strong>
                </span>
              </>
            )}
            {suiteMeta?.updated_at && (
              <>
                <span aria-hidden="true" className="text-suite-neutral-300">·</span>
                <span>aggiornato il {formatDateIT(suiteMeta.updated_at)}</span>
              </>
            )}
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

export default SuiteLauncher;
